/* eslint-disable react/prop-types */

import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import DatePicker from 'react-datepicker';
import TableComponent from './tableComponent';
import { sessionObjects } from '../session/sessionData';
import Select from 'react-select'; // Import react-select for multi-select functionality

const ReportComponent = ({ keyId }) => {
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [filterFields, setFilterFields] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [tableLoading, setTableLoading] = useState(true);

  // Check if current report should use multi-select for commodity
  const isMultiSelectReport =
    keyId === 'farmer_dashboard_records_fcs' ||
    keyId === 'milling_dispatch_dashboard_report_fcs';

  // Determine API host based on environment
  const domain = window.location.hostname;
  let xHost = '';
  if (domain.includes('.uat')) {
    xHost = 'fcs-api.uat.farmsio.com';
  } else {
    xHost = 'fcs-beam-api-v2.farmsio.com';
  }

  const headers = {
    'x-client-host': xHost,
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionObjects.access_token}`
  };

  const apiBaseUrl = `${process.env.REACT_APP_FCS_API_URL}`;

  // Function to fetch filter dropdown options for a specific model
  const fetchDropdownOptions = async (dataModel, isInitialLoad = false) => {
    try {
      const url = `${apiBaseUrl}api/sqladmin/get-dropdown-data-unauthenticated`;
      const requestBody = {
        userId: sessionObjects.applicationUserId,
        dataModel: dataModel,
        id: '',
        searchString: '',
        param1: '',
        param2: '',
        param3: '',
        param4: '',
        param5: '',
        param6: '',
        param7: '',
        param8: '',
        param9: '',
        param10: '',
        sessionObjects: sessionObjects
      };

      const response = await axios.post(url, requestBody, { headers });

      if (response.data && response.data.Status === 'SUCCESS') {
        return response.data.Result || [];
      }
      return [];
    } catch (error) {
      console.error(`Error fetching dropdown options for ${dataModel}:`, error);
      return [];
    }
  };
  // Function to fetch data based on filters
  const fetchData = async (filters = {}) => {
    setTableLoading(true);
    setError(null);

    try {
      const url = `${apiBaseUrl}api/documents/get-list-record-sets`;

      // Build params in sequence for the API
      const filterValues = [];

      // Make a copy of filters to modify for multi-select
      const processedFilters = { ...filters };

      // If we have filterFields, use them to order the params properly
      if (filterFields.length > 0) {
        filterFields.forEach(field => {
          // For commodity field in multi-select reports
          if (
            isMultiSelectReport &&
            (field.modelName === 'commodity' ||
              field.modelName === 'crop_type_fcs')
          ) {
            let value = processedFilters[field.modelName] || '';

            // Check if the value is an array (from multi-select)
            if (Array.isArray(value)) {
              // Convert array to comma-separated string for API
              value = value.join(',');
              processedFilters[field.modelName] = value;
            }
          }

          const value = processedFilters[field.modelName] || '';
          filterValues.push(value);
        });
      } else {
        // If no filter fields are available yet, just use the filter values in any order
        Object.values(processedFilters).forEach(value => {
          filterValues.push(value);
        });
      }

      // Ensure we don't exceed 10 params
      const safeFilterValues = filterValues.slice(0, 10);

      const requestBody = {
        userId: sessionObjects.applicationUserId,
        key: keyId,
        id: safeFilterValues[0] || '',
        param1: safeFilterValues[1] || '',
        param2: safeFilterValues[2] || '',
        param3: safeFilterValues[3] || '',
        param4: safeFilterValues[4] || '',
        param5: safeFilterValues[5] || '',
        param6: safeFilterValues[6] || '',
        param7: safeFilterValues[7] || '',
        param8: safeFilterValues[8] || '',
        param9: safeFilterValues[9] || '',
        param10: '',
        limit: '100',
        offset: '0',
        sessionObjects: sessionObjects
      };

      const response = await axios.post(url, requestBody, { headers });

      if (response.data && response.data.Status === 'SUCCESS') {
        const result = response.data.Result;

        // Check if there's at least one recordView
        if (result.recordsView && result.recordsView.length > 0) {
          const recordView = result.recordsView[0];

          // Set the filter fields if they exist and we're initially loading
          if (recordView.filterFields && loading) {
            // Check if there's already a date filter in the filters
            const hasDateFilter = recordView.filterFields.some(
              field => field.inputType === 'date'
            );

            // If no date filter exists, add one
            let updatedFilterFields = [...recordView.filterFields];
            setFilterFields(updatedFilterFields);

            // Set default filter values from the field values
            const defaultFilters = {};
            updatedFilterFields.forEach(field => {
              if (field.value && field.value.value) {
                defaultFilters[field.modelName] = field.value.value;
              }
            });

            // Only set default filters if we don't already have selections
            if (Object.keys(selectedFilters).length === 0) {
              setSelectedFilters(defaultFilters);
            }

            // Fetch options for each dropdown filter field
            const optionsPromises = updatedFilterFields
              .filter(field => field.inputType === 'dropdown')
              .map(async field => {
                const options = await fetchDropdownOptions(field.dataModel);
                return { [field.modelName]: options };
              });

            const allOptions = await Promise.all(optionsPromises);
            const mergedOptions = allOptions.reduce(
              (acc, curr) => ({ ...acc, ...curr }),
              {}
            );
            setFilterOptions(mergedOptions);
          }

          setApiData(recordView);
        } else {
          setApiData(null);
          setError('No data records found in the response');
        }
      } else {
        setApiData(null);
        setError(response.data?.Message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('API Error:', error);
      setApiData(null);
      setError(error.message || 'An error occurred while fetching data');
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  };

  // Fetch initial data when component mounts
  useEffect(() => {
    fetchData();
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [field.modelName]: value
    }));
  };

  // Handle multi-select change
  const handleMultiSelectChange = (field, selectedOptions) => {
    // Extract the values from the selected options
    const values = selectedOptions.map(option => option.value);
    setSelectedFilters(prev => ({
      ...prev,
      [field.modelName]: values
    }));
  };

  // Handle filter submission
  const handleFilterSubmit = e => {
    e.preventDefault();
    fetchData(selectedFilters);
  };

  // Function to format Date to string (DD/MM/YYYY)
  const formatDate = dateInput => {
    // If the input is already a string in DD/MM/YYYY format, return it
    if (
      typeof dateInput === 'string' &&
      /^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)
    ) {
      return dateInput;
    }

    // Handle string dates in various formats
    if (typeof dateInput === 'string') {
      try {
        // Create a new Date object from the string
        const parsedDate = new Date(dateInput);
        if (!isNaN(parsedDate.getTime())) {
          const day = String(parsedDate.getDate()).padStart(2, '0');
          const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
          const year = parsedDate.getFullYear();
          return `${day}/${month}/${year}`;
        }
      } catch (error) {
        console.error('Error formatting string date:', error);
        return '';
      }
    }

    // Handle Date objects
    if (dateInput instanceof Date && !isNaN(dateInput.getTime())) {
      try {
        const day = String(dateInput.getDate()).padStart(2, '0');
        const month = String(dateInput.getMonth() + 1).padStart(2, '0');
        const year = dateInput.getFullYear();
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting Date object:', error);
        return '';
      }
    }

    console.warn('Could not format date input:', dateInput);
    return '';
  };

  // Function to parse a date string to Date object
  const parseDate = dateString => {
    if (!dateString) return null;

    try {
      // Handle different date formats
      if (typeof dateString === 'string') {
        // Check if it's already in DD/MM/YYYY format
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
          const [day, month, year] = dateString.split('/').map(Number);
          // Validate the date components before creating the Date object
          if (
            isNaN(day) ||
            isNaN(month) ||
            isNaN(year) ||
            day < 1 ||
            day > 31 ||
            month < 1 ||
            month > 12 ||
            year < 1900
          ) {
            return null;
          }
          return new Date(year, month - 1, day);
        }
        // Handle API date format (e.g. "Mon Aug 08 2022 00:00:00 GMT+0000")
        else if (dateString.includes('GMT')) {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date;
          }
          return null;
        }
        // Try default Date parsing as a fallback
        else {
          const date = new Date(dateString);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error parsing date:', error);
      return null;
    }
  };
  // Handle date picker change
  const handleDateChange = (field, date) => {
    if (date && !isNaN(date.getTime())) {
      const fieldName = field.modelName;
      setSelectedFilters(prev => ({
        ...prev,
        [fieldName]: formatDate(date)
      }));
    }
  };

  // Format options for react-select
  const formatOptionsForReactSelect = options => {
    return options.map(option => ({
      value: option.id,
      label: option.value
    }));
  };
  const customStyles = {
    control: provided => ({
      ...provided,
      // overflowY: 'scroll',
      // whiteSpace: 'nowrap',
      // textOverflow: 'ellipsis',
      // height: '40px',
      minHeight: '35px'
    }),
    valueContainer: provided => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflowY: 'auto',
      textOverflow: 'ellipsis',
      // maxWidth: 'calc(100% - 8px)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      //   maxWidth: '200px',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      // background: 'red',
      maxHeight: '90px'
    }),

    multiValue: provided => ({
      ...provided,
      // display: 'none',
      height: 'fit-content'
    }),
    multiValueLabel: provided => ({
      ...provided
      // display: 'none'
      // backgroundColor: 'green',
      // height: '20px'
    })
  };
  // DatePicker styles
  const datePickerStyles = `
    .custom-datepicker-container {
      width: 100%;
      position: relative;
    }
    .custom-datepicker-wrapper {
      width: 100%;
    }
    .custom-datepicker {
      width: 100%;
      height: 35px;
      border-radius: 0.25rem;
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
    .react-datepicker-popper {
      z-index: 9999 !important;
    }
    
    /* Ensure consistent heights for all form controls */
    .form-control, .form-select, .react-select__control {
      min-height: 35px !important;
      height: 35px;
    }
    
    /* Align all form elements bottom */
    .form-group {
      margin-bottom: 0;
    }
    
    /* Make sure labels have consistent height */
    .form-label {
      margin-bottom: 0.25rem;
    }
  `;

  return (
    <div>
      {' '}
      {/* Filter Form */}
      {filterFields.length > 0 && (
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Filter Report</h5>
          </Card.Header>
          <Card.Body>
            {/* Add DatePicker styles */}
            <style>{datePickerStyles}</style>
            <Form onSubmit={handleFilterSubmit}>
              <Row className="d-flex align-items-start">
                {filterFields.map((field, index) => {
                  if (field.inputType === 'date') {
                    return (
                      <Col key={field.modelName} className="mb-0">
                        {' '}
                        <Form.Group className="mb-0">
                          <Form.Label>{field.placeHolder || 'Date'}</Form.Label>
                          <div className="custom-datepicker-container">
                            <DatePicker
                              selected={parseDate(
                                selectedFilters[
                                  field.modelName || 'report_date'
                                ]
                              )}
                              onChange={date => handleDateChange(field, date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control form-control-sm py-1 custom-datepicker"
                              wrapperClassName="custom-datepicker-wrapper"
                              placeholderText={`Select ${
                                field.placeHolder || 'Date'
                              }`}
                              required={field.isRequired}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    );
                  } else if (field.inputType === 'dropdown') {
                    // Check if this is a commodity or crop_type_fcs field in a multi-select report
                    const isCommodityOrCropTypeMultiSelect =
                      isMultiSelectReport &&
                      (field.modelName === 'commodity' ||
                        field.modelName === 'crop_type_fcs');

                    if (isCommodityOrCropTypeMultiSelect) {
                      return (
                        <Col md={3} key={field.modelName} className="mb-0">
                          {' '}
                          <Form.Group className="mb-0">
                            <Form.Label>{field.placeHolder}</Form.Label>
                            <Select
                              isMulti
                              closeMenuOnSelect={false}
                              name={field.modelName}
                              placeholder={`Select ${field.placeHolder}`}
                              options={formatOptionsForReactSelect(
                                filterOptions[field.modelName] || []
                              )}
                              className="basic-multi-select"
                              classNamePrefix="select"
                              onChange={selected =>
                                handleMultiSelectChange(field, selected)
                              }
                              styles={customStyles}
                              required={field.isRequired}
                            />
                          </Form.Group>
                        </Col>
                      );
                    }

                    return (
                      <Col md={3} key={field.modelName} className="mb-0">
                        {' '}
                        <Form.Group className="mb-0">
                          <Form.Label>{field.placeHolder}</Form.Label>
                          <Form.Select
                            value={selectedFilters[field.modelName] || ''}
                            onChange={e =>
                              handleFilterChange(field, e.target.value)
                            }
                            required={field.isRequired}
                            className="py-1"
                          >
                            <option value="">Select {field.placeHolder}</option>
                            {filterOptions[field.modelName]?.map(option => (
                              <option key={option.id} value={option.id}>
                                {option.value}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    );
                  }
                  return null;
                })}
                <Col className="mt-4" style={{ maxWidth: '200px' }}>
                  <Button variant="primary" type="submit" className="w-100">
                    Apply Filters
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}
      {/* Table Component with Loading State */}
      {tableLoading ? (
        <Card>
          <Card.Body className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading data...</p>
          </Card.Body>
        </Card>
      ) : !isEmpty(apiData) && apiData.datas && apiData.datas.length > 0 ? (
        <TableComponent data={apiData} />
      ) : (
        <Card>
          <Card.Body className="text-center py-5">
            <h5>No Data Available</h5>
            <p>Please apply different filters</p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ReportComponent;
